import ApprovalTokenManager from '../utils/ApprovalTokenManager';
import { ApprovalRequest } from '../types/User';

interface EmailTemplate {
  subject: string;
  html: string;
  text: string;
}

class EmailService {
  // Generate approval email template
  static generateApprovalEmail(request: ApprovalRequest): EmailTemplate {
    const urls = ApprovalTokenManager.generateApprovalUrls(
      request.id,
      request.managerEmail,
      request.userId,
      request.action,
      request.roles
    );

    const actionText = this.getActionText(request.action);
    const subject = `NYX+ Approval Required: ${actionText} for ${request.userName}`;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>NYX+ Approval Request</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #3b82f6; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
          .content { background: white; padding: 30px; border: 1px solid #e5e7eb; }
          .footer { background: #f9fafb; padding: 20px; text-align: center; border-radius: 0 0 8px 8px; border: 1px solid #e5e7eb; border-top: none; }
          .button { display: inline-block; padding: 12px 24px; margin: 10px 5px; text-decoration: none; border-radius: 6px; font-weight: 500; }
          .approve-btn { background: #10b981; color: white; }
          .reject-btn { background: #ef4444; color: white; }
          .details-btn { background: #6b7280; color: white; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 6px; margin: 20px 0; }
          .role-tag { display: inline-block; background: #e5e7eb; padding: 4px 8px; margin: 2px; border-radius: 4px; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🛡️ NYX+ Approval Required</h1>
            <p>Role Change Request</p>
          </div>
          
          <div class="content">
            <h2>Hello ${request.managerName},</h2>
            
            <p>A role change request has been submitted for one of your team members and requires your approval.</p>
            
            <div class="info-box">
              <h3>Request Details:</h3>
              <p><strong>Employee:</strong> ${request.userName}</p>
              <p><strong>Requested by:</strong> ${request.requestedBy}</p>
              <p><strong>Action:</strong> ${actionText}</p>
              <p><strong>Requested:</strong> ${request.requestedAt.toLocaleDateString()}</p>
              
              <h4>Roles to ${request.action === 'add' ? 'Add' : request.action === 'remove' ? 'Remove' : 'Change'}:</h4>
              ${request.roles.map(role => `<span class="role-tag">${role}</span>`).join('')}
              
              <h4>Current Roles:</h4>
              ${request.currentRoles.map(role => `<span class="role-tag">${role}</span>`).join('')}
            </div>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${urls.approveUrl}" class="button approve-btn">✅ Approve Request</a>
              <a href="${urls.rejectUrl}" class="button reject-btn">❌ Reject Request</a>
            </div>
            
            <div style="text-align: center; margin: 20px 0;">
              <a href="${urls.baseUrl}" class="button details-btn">📋 View Full Details</a>
            </div>
            
            <p><strong>Important:</strong> This approval link will expire in 48 hours for security reasons.</p>
            
            <p>If you have any questions about this request, please contact the requester directly.</p>
          </div>
          
          <div class="footer">
            <p>This is an automated message from NYX+ Access Management Portal</p>
            <p>If you did not expect this email, please contact your IT administrator</p>
          </div>
        </div>
      </body>
      </html>
    `;

    const text = `
NYX+ Approval Required: ${actionText} for ${request.userName}

Hello ${request.managerName},

A role change request has been submitted for one of your team members and requires your approval.

Request Details:
- Employee: ${request.userName}
- Requested by: ${request.requestedBy}
- Action: ${actionText}
- Requested: ${request.requestedAt.toLocaleDateString()}

Roles to ${request.action === 'add' ? 'Add' : request.action === 'remove' ? 'Remove' : 'Change'}: ${request.roles.join(', ')}
Current Roles: ${request.currentRoles.join(', ')}

Quick Actions:
- Approve: ${urls.approveUrl}
- Reject: ${urls.rejectUrl}
- View Details: ${urls.baseUrl}

This approval link will expire in 48 hours for security reasons.

If you have any questions about this request, please contact the requester directly.

---
This is an automated message from NYX+ Access Management Portal
If you did not expect this email, please contact your IT administrator
    `;

    return { subject, html, text };
  }

  private static getActionText(action: string): string {
    switch (action) {
      case 'add':
        return 'Add Roles';
      case 'remove':
        return 'Remove Roles';
      case 'change':
        return 'Change Roles';
      default:
        return 'Modify Roles';
    }
  }

  // Simulate sending email (replace with actual email service like SendGrid)
  static async sendApprovalEmail(request: ApprovalRequest): Promise<boolean> {
    try {
      const emailTemplate = this.generateApprovalEmail(request);
      
      // In production, integrate with SendGrid, AWS SES, or Azure Communication Services
      console.log('📧 Email would be sent to:', request.managerEmail);
      console.log('📧 Subject:', emailTemplate.subject);
      console.log('📧 Approval URL:', emailTemplate.html.includes('approve') ? 'Generated' : 'Not found');
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return true;
    } catch (error) {
      console.error('Failed to send approval email:', error);
      return false;
    }
  }
}

export default EmailService;
